import { useState, useCallback, useEffect } from "react";
import SntActionButton from "@cleanup/components/Buttons/SntActionButton";
import SntFavoriteIcon from "@icons/SntFavoriteIcon";
import SntTooltip from "@cleanup/components/Tooltip/SntTooltip";
import { useFavorites } from "@/contexts/FavoritesContext";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

export type FavoriteEntityType = "organization" | "device" | "dashboard" | "geozone";

export interface FavoriteButtonProps {
  entityType: FavoriteEntityType;
  entityId: string | number;
  onToggle?: (newState: boolean, entityType: FavoriteEntityType, entityId: string | number) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  tooltip?: boolean;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  entityType,
  entityId,
  onToggle,
  disabled = false,
  size = "sm",
  className = "",
  tooltip = true,
}) => {
  const {
    isFavorite: checkIsFavorite,
    toggleFavorite,
    loadFavoriteIds,
    initialized,
  } = useFavorites();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Load favorite IDs for this entity type if not already loaded
  useEffect(() => {
    if (!initialized[entityType]) {
      loadFavoriteIds(entityType);
    }
  }, [entityType, initialized, loadFavoriteIds]);

  const isFavorite = checkIsFavorite(entityType, entityId);

  const handleToggle = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled || isLoading) return;

      setIsLoading(true);

      try {
        const newState = await toggleFavorite(entityType, entityId);

        // Notify parent component
        onToggle?.(newState, entityType, entityId);
      } catch (error) {
        // Error handling is done in the context
        console.error("Failed to toggle favorite:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [entityType, entityId, disabled, isLoading, onToggle, toggleFavorite]
  );

  const button = (
    <SntActionButton
      onClick={handleToggle}
      disabled={disabled || isLoading}
      variant="sensolus-transparent"
      size={size}
      className={`favorite-button ${className}`}
      aria-label={
        isFavorite
          ? translate(LanguageKey.remove_from_favorites_key)
          : translate(LanguageKey.add_to_favorites_key)
      }
      role="button"
    >
      <SntFavoriteIcon
        size={size === "sm" ? 16 : 20}
        color={isFavorite ? "#ffc107" : "#ffffff"}
        stroke={isFavorite ? "none" : "#6c757d"}
        style={{ opacity: isLoading ? 0.5 : 1 }}
      />
    </SntActionButton>
  );

  if (tooltip) {
    return (
      <SntTooltip
        text={
          isLoading
            ? translate(LanguageKey.updating_key)
            : isFavorite
            ? translate(LanguageKey.remove_from_favorites_key)
            : translate(LanguageKey.add_to_favorites_key)
        }
        icon={button}
      />
    );
  }

  return button;
};

export default FavoriteButton;